import type { AttributeRule } from './index';

/*
 * Feature flags (P-PER-59). A flag is an experiments row of type 'Feature Flag' with two
 * variants, Off and On, plus a flag_rollouts row holding how much traffic sees On and how that
 * share moves. Design: the Linear project document "Feature flags: design".
 */

/**
 * What a flag's On side is. 'code': the customer's own code asks whether it is on (the server
 * SDK or the browser API). 'editor': the On variant carries visual-editor changes, applied by
 * the runtime like a variant's, for fixes that are not experiments.
 */
export const FLAG_KINDS = ['code', 'editor'] as const;
export type FlagKind = (typeof FLAG_KINDS)[number];

/**
 * Where a flag's rollout plan is. 'none': no plan, the rollout is set by hand. 'active': the
 * scheduler advances it. 'paused': held where it is (by hand, or a guardrail breach in M2).
 * 'done': the plan reached its last step.
 */
export const FLAG_PLAN_STATUSES = ['none', 'active', 'paused', 'done'] as const;
export type FlagPlanStatus = (typeof FLAG_PLAN_STATUSES)[number];

/**
 * What lets a plan step advance: its minimum time ('time'), a passing guardrail ('kpi'), or
 * both (PER-340). A 'kpi' step needs a primary goal and the website's visitor ID: the guardrail
 * compares the visitors each side enrolled during the step.
 */
export const FLAG_STEP_GATES = ['time', 'kpi', 'both'] as const;
export type FlagStepGate = (typeof FLAG_STEP_GATES)[number];
export const ACCEPTED_FLAG_STEP_GATES: readonly FlagStepGate[] = FLAG_STEP_GATES;

/** Which side a ring serves its members. */
export const FLAG_RING_SERVES = ['on', 'off'] as const;
export type FlagRingServe = (typeof FLAG_RING_SERVES)[number];

/** Rollouts are in basis points: 10000 is everyone. */
export const FLAG_FULL_ROLLOUT_BP = 10_000;

export const MAX_FLAG_STEPS = 10;
export const MAX_FLAG_RINGS = 10;
export const MAX_FLAG_RING_IDS = 1000;
export const MAX_FLAG_RING_ID_LENGTH = 256;

/** The longest a single plan step may last: a year, so a typo cannot freeze a plan for ever. */
export const MAX_FLAG_STEP_DURATION_MS = 365 * 24 * 60 * 60 * 1000;

/**
 * One step of a rollout plan: the share of traffic that sees On while the step is current, and
 * what must hold before the plan moves to the next step. minDurationMs and notBefore are both
 * optional; a step with neither advances on the scheduler's next tick.
 */
export type FlagStep = {
  rolloutBp: number;
  minDurationMs?: number;
  /** Epoch ms. */
  notBefore?: number;
  gate: FlagStepGate;
};

/**
 * A group decided before the percentage: staff, a beta list, key accounts. A visitor is in the
 * ring when every rule matches their attributes and, when ids is set, their ID is in it. The
 * first matching ring wins, and ring members are not part of the comparison.
 */
export type FlagRing = {
  rules: AttributeRule[];
  ids?: string[];
  serve: FlagRingServe;
};

/**
 * The two variants every flag has. Off is stored as 'Original', the name the stats, the monitor
 * and the editor already treat as the control; the flag UI shows it as Off.
 */
export const FLAG_OFF_VARIANT = { name: 'Original', key: 'off' } as const;
export const FLAG_ON_VARIANT = { name: 'On', key: 'on' } as const;

/** Days at 100% after which an editor flag is put to the Implementation queue. */
export const FLAG_IMPLEMENT_NUDGE_DAYS = 30;

/**
 * How much worse On may be than Off on the primary goal before a step is a breach, relative, in
 * basis points: 200 is "On converts at least 98% as well as Off" (decided 2026-10-27).
 */
export const FLAG_GUARDRAIL_DEFAULT_MARGIN_BP = 200;
export const FLAG_GUARDRAIL_MAX_MARGIN_BP = 5000;

/**
 * What a step needs before the guardrail gives a verdict other than 'collecting': visitors per
 * side, and conversions on Off (why these two: crons/experiment-stats/flag-guardrails/
 * guardrail-stats.ts). Here so the flag page says the same numbers the check uses.
 */
export const FLAG_GUARDRAIL_MIN_VISITORS_PER_ARM = 200;
export const FLAG_GUARDRAIL_MIN_OFF_CONVERSIONS = 1000;

/**
 * What the guardrail says about the current step.
 * - 'collecting': not enough data for a verdict yet.
 * - 'clear': enough data, and no harm detected. A KPI step advances on it (decided 2026-10-27:
 *   the guardrail stops harm rather than demanding proof of safety, which would take about a
 *   million visitors a side at a 5% rate and stall most rollouts).
 * - 'pass': On is proven no worse than the margin allows. Advances too.
 * - 'breach': On is worse than the margin allows, with confidence; the plan is paused.
 */
export const FLAG_GUARDRAIL_VERDICTS = ['collecting', 'clear', 'pass', 'breach'] as const;
export type FlagGuardrailVerdict = (typeof FLAG_GUARDRAIL_VERDICTS)[number];

/** One side of a step's comparison: visitors first enrolled during the step, and how many converted. */
export type FlagGuardrailArm = { visitors: number; conversions: number };

/**
 * flag_rollouts.guardrail: the margin, and the last check of the current step (Crons Experiment
 * Stats writes it every five minutes; the scheduler and the flag page read it). `step` and
 * `stepStartedAt` say which step the check is about, so a check of an earlier step never
 * advances a later one.
 */
export type FlagGuardrail = {
  marginBp: number;
  check?: {
    step: number;
    stepStartedAt: number;
    checkedAt: number;
    /** The primary goal's event name, as resolved when checked. */
    event: string;
    verdict: FlagGuardrailVerdict;
    on: FlagGuardrailArm;
    off: FlagGuardrailArm;
    /**
     * The always-valid interval for On's rate relative to Off's, minus one: -0.05 is "5% worse".
     * Null until both sides have enough visitors.
     */
    relative: { lower: number; upper: number } | null;
  };
};
